import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { memo, useState } from 'react';
import { OBJECT_PARAM_KEY_PATTERN, type NumberBinding, type ObjectParam } from 'shared-types';
import { T, useT } from '../../../translation/src';
import { deleteSelection } from '../document/actions';
import { updateParam } from '../document/specOps';
import { useEditorState, useObjectEditor } from '../EditorContext';
import { FieldRow, NumberField, PanelButton, PanelSection, Segmented, TextField, ToggleRow } from '../panels/fields';

/** The selected parameter: its label, default and limits, choice options, and what uses it. */
const ParamInspector_: React.FC<{ paramKey: string }> = ({ paramKey }) => {
  const { store } = useObjectEditor();
  const { spec } = useEditorState();
  const param = spec.params.find((p) => p.key === paramKey);
  const keyTaken = useT('this key is taken', { swc: true });
  const keyInvalid = useT('letters and digits only, starting with a letter', { swc: true });
  const [keyError, setKeyError] = useState<string | null>(null);
  if (!param) return null;
  const update = (next: ObjectParam) => store.apply((s) => updateParam(s, paramKey, next), { kind: 'param', key: next.key });

  const users = spec.groups.filter((g) => {
    const r = g.repeat;
    const bound = (b: NumberBinding | undefined) => !!b && 'param' in b && b.param === paramKey;
    return g.visibleWhen?.param === paramKey || (r?.kind === 'linear' && (bound(r.pitch) || bound(r.count))) || (r?.kind === 'radial' && bound(r.count));
  });

  return (
    <>
      <PanelSection title={<T _str="parameter" />}>
        <FieldRow label={<T _str="label" swc />} hint={<T _str="what users see in the properties panel" swc />}>
          <TextField value={param.label} maxLength={80} onCommit={(label) => label.trim() && update({ ...param, label: label.trim() })} />
        </FieldRow>
        <FieldRow label={<T _str="key" swc />} hint={keyError ?? <T _str="stored in projects; avoid changing it after publishing" swc />}>
          <TextField
            value={param.key}
            maxLength={32}
            onCommit={(key) => {
              if (!OBJECT_PARAM_KEY_PATTERN.test(key)) return setKeyError(keyInvalid);
              if (spec.params.some((p) => p.key === key && p.key !== paramKey)) return setKeyError(keyTaken);
              setKeyError(null);
              update({ ...param, key });
            }}
          />
        </FieldRow>

        {(param.kind === 'float' || param.kind === 'int') && (
          <>
            <FieldRow label={<T _str="default" swc />}>
              <NumberField value={param.default} integer={param.kind === 'int'} unit={param.kind === 'float' && param.distance ? 'm' : undefined} onCommit={(v) => update({ ...param, default: v })} />
            </FieldRow>
            <FieldRow label={<T _str="min / max" swc />}>
              <span className="grid grid-cols-2 gap-1">
                <NumberField value={param.min ?? 0} integer={param.kind === 'int'} onCommit={(min) => update({ ...param, min: Math.min(min, param.default) })} />
                <NumberField value={param.max ?? param.default * 10} integer={param.kind === 'int'} onCommit={(max) => update({ ...param, max: Math.max(max, param.default) })} />
              </span>
            </FieldRow>
            {param.kind === 'float' && <ToggleRow label={<T _str="is a length" swc />} hint={<T _str="shown in the user's meters or feet" swc />} checked={!!param.distance} onChange={(distance) => update({ ...param, distance })} />}
          </>
        )}

        {param.kind === 'boolean' && (
          <FieldRow label={<T _str="default" swc />}>
            <Segmented
              value={param.default ? 'on' : 'off'}
              options={[
                { value: 'on', label: <T _str="on" /> },
                { value: 'off', label: <T _str="off" /> },
              ]}
              onChange={(v) => update({ ...param, default: v === 'on' })}
            />
          </FieldRow>
        )}

        {param.kind === 'select' && (
          <div className="flex flex-col gap-1">
            <span className="text-sm text-gray-600 dark:text-gray-300">
              <T _str="options (the marked one is the default)" swc />
            </span>
            {param.options.map((option, i) => (
              <div key={option.value} className="flex items-center gap-1">
                <input type="radio" checked={param.default === option.value} onChange={() => update({ ...param, default: option.value })} aria-label="default" />
                <TextField value={option.label} maxLength={80} onCommit={(label) => update({ ...param, options: param.options.map((o, j) => (j === i ? { ...o, label } : o)) })} />
                <PanelButton
                  danger
                  disabled={param.options.length <= 1}
                  onClick={() => {
                    const options = param.options.filter((_, j) => j !== i);
                    update({ ...param, options, default: param.default === option.value ? options[0].value : param.default });
                  }}
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </PanelButton>
              </div>
            ))}
            <PanelButton
              onClick={() => {
                const value = `o${Date.now().toString(36)}`;
                update({ ...param, options: [...param.options, { value, label: `Option ${param.options.length + 1}` }] });
              }}
            >
              <PlusIcon className="h-3.5 w-3.5" />
              <T _str="add option" swc />
            </PanelButton>
          </div>
        )}
      </PanelSection>

      <PanelSection title={<T _str="used by" />}>
        {users.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            <T _str="nothing yet: bind a repeat's spacing or count, or a group's visibility to it" swc />
          </p>
        ) : (
          users.map((g) => (
            <button key={g.id} type="button" onClick={() => store.select({ kind: 'group', id: g.id })} className="text-left text-sm text-french-violet-600 hover:underline dark:text-neon-green-300">
              {g.name}
            </button>
          ))
        )}
        <PanelButton danger onClick={() => deleteSelection(store)}>
          <TrashIcon className="h-3.5 w-3.5" />
          <T _str="delete parameter" swc />
        </PanelButton>
      </PanelSection>
    </>
  );
};

export const ParamInspector = memo(ParamInspector_);
